import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';

/** @type {import('../../../contracts/forms.js').FieldDriver} */
export const radioDriver = {
  type: 'radio',
  mount(container, ctx) {
    const { def, onChange } = ctx;
    const radioDef = /** @type {import('../../../contracts/forms.js').RadioFieldDef} */ (def);

    const group = createElement('div', {
      class: 'radio-group',
      role: 'group',
      'aria-label': def.label,
    });
    if (def.required) group.setAttribute('aria-required', 'true');
    container.appendChild(group);

    if (radioDef.loadOptions) {
      // --- Async path ---
      let destroyed = false;
      let currentValue = ctx.value != null ? String(ctx.value) : '';
      /** @type {ReadonlyArray<import('../../../contracts/forms.js').FieldOption> | null} */
      let prevOptions = null;
      /** @type {HTMLInputElement[]} */
      let radios = [];
      let forcedDisabled = false;

      const provider = createOptionsProvider({
        options: radioDef.options,
        loadOptions: radioDef.loadOptions,
        mapOption: radioDef.mapOption,
      });

      function applyDisabled() {
        for (const radio of radios) {
          radio.disabled = forcedDisabled || Boolean(def.disabled);
        }
      }

      /**
       * @param {import('../../../contracts/forms.js').FieldOption[]} options
       */
      function renderRadios(options) {
        group.textContent = '';
        radios = [];
        for (const opt of options) {
          const radioId = `field-${def.key}-${opt.value}`;
          const radioLabel = createElement('label', { class: 'radio-label', for: radioId });
          const radio = /** @type {HTMLInputElement} */ (createElement('input', {
            type: 'radio',
            id: radioId,
            name: `field-${def.key}`,
            value: opt.value,
          }));
          if (currentValue === opt.value) radio.checked = true;
          if (forcedDisabled || Boolean(def.disabled)) radio.disabled = true;
          radio.addEventListener('change', () => {
            if (radio.checked) {
              currentValue = radio.value;
              onChange(currentValue);
            }
          });
          radios.push(radio);
          radioLabel.appendChild(radio);
          radioLabel.appendChild(document.createTextNode(` ${opt.label}`));
          group.appendChild(radioLabel);
        }
      }

      /**
       * @param {import('../../../contracts/forms.js').FieldOption[]} options
       */
      function rebuildRadios(options) {
        const prevValue = currentValue;
        renderRadios(options);
        const valueExists = options.some(o => o.value === currentValue);
        if (!valueExists && currentValue !== '') {
          currentValue = '';
        }
        if (currentValue !== prevValue) {
          onChange(currentValue);
        }
      }

      const unsubscribe = subscribeWithInitial(provider, (state) => {
        if (destroyed) return;
        group.setAttribute('aria-busy', state.loading ? 'true' : 'false');
        if (state.options !== prevOptions) {
          if (prevOptions === null) {
            prevOptions = state.options;
            renderRadios(state.options);
          } else {
            prevOptions = state.options;
            rebuildRadios(state.options);
          }
        }
      });

      provider.load('');

      return {
        setValue(value) {
          currentValue = value != null ? String(value) : '';
          for (const radio of radios) {
            radio.checked = radio.value === currentValue;
          }
        },
        getValue() {
          return currentValue;
        },
        setErrors() {
          // errors rendered by host
        },
        destroy() {
          destroyed = true;
          unsubscribe();
          provider.destroy();
          group.remove();
        },
        setDisabled(disabled) {
          forcedDisabled = disabled;
          applyDisabled();
        },
        clearValue() {
          currentValue = '';
          for (const radio of radios) {
            radio.checked = false;
          }
        },
        getOptionsProvider() {
          return provider;
        },
        setAriaInvalid(invalid) {
          group.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        },
      };
    }

    // --- Static path ---
    /** @type {HTMLInputElement[]} */
    const radios = [];
    let forcedDisabled = false;

    for (const opt of radioDef.options ?? []) {
      const radioId = `field-${def.key}-${opt.value}`;
      const radioLabel = createElement('label', { class: 'radio-label', for: radioId });
      const radio = /** @type {HTMLInputElement} */ (createElement('input', {
        type: 'radio',
        id: radioId,
        name: `field-${def.key}`,
        value: opt.value,
      }));
      if (def.disabled) radio.disabled = true;
      if (ctx.value != null && String(ctx.value) === opt.value) radio.checked = true;
      radio.addEventListener('change', () => {
        if (radio.checked) onChange(radio.value);
      });
      radios.push(radio);
      radioLabel.appendChild(radio);
      radioLabel.appendChild(document.createTextNode(` ${opt.label}`));
      group.appendChild(radioLabel);
    }

    return {
      setValue(value) {
        const strValue = value != null ? String(value) : '';
        for (const radio of radios) {
          radio.checked = radio.value === strValue;
        }
      },
      getValue() {
        const checked = radios.find(r => r.checked);
        return checked ? checked.value : '';
      },
      setErrors() {
        // errors rendered by host
      },
      destroy() {
        group.remove();
      },
      setDisabled(disabled) {
        forcedDisabled = disabled;
        for (const radio of radios) {
          radio.disabled = forcedDisabled || Boolean(def.disabled);
        }
      },
      clearValue() {
        for (const radio of radios) {
          radio.checked = false;
        }
      },
      setAriaInvalid(invalid) {
        group.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      },
    };
  },
};
